import type { components } from "@/schema";

// Re-export schema types for convenience
type ProjectDetail = components["schemas"]["ProjectDetail"];
type ItineraryDayWithItems = components["schemas"]["ItineraryDayWithItems"];
type ItineraryArrangementWithItineraryItem =
  components["schemas"]["ItineraryArrangementWithItineraryItem"];
type ProjectPaymentSummary = components["schemas"]["ProjectPaymentSummary"];
type ProjectProfitSummary = components["schemas"]["ProjectProfitSummary"];

// --- Aggregated project data from Rails API ---

export interface AggregatedProjectData {
  project: ProjectDetail;
  itineraries: ItineraryDayWithItems[];
  arrangements: ItineraryArrangementWithItineraryItem[];
  paymentSummary: ProjectPaymentSummary | null;
  profitSummary: ProjectProfitSummary | null;
  fetchedAt: string;
  mergeCheckContext?: ProjectDataMergeCheckContext;
}

export type ProjectDataSourceKind =
  | "crm-api"
  | "excel"
  | "pdf"
  | "travefy"
  | "html"
  | "web"
  | "manual-json"
  | "merged";

export type ProjectDataTrustLevel =
  | "authoritative"
  | "customer-facing"
  | "supplemental"
  | "experimental";

export type MergeCheckStageKey = "base" | "candidate" | "merged";

export interface ProjectDataSourceDescriptor {
  sourceId: string;
  label: string;
  kind: ProjectDataSourceKind;
  trustLevel: ProjectDataTrustLevel;
  dataDigest: string;
  origin?: {
    crmProjectId?: number;
    fileName?: string;
    mimeType?: string;
    size?: number;
    sha256?: string;
    url?: string;
    converterName?: string;
    converterVersion?: string;
    generatedAt?: string;
    cacheKey?: string;
  };
}

export interface MergeCheckCacheMetadata {
  mergeCacheKey: string;
  rootMergeCacheKey?: string;
  stage?: MergeCheckStageKey;
  sourceLabel: string;
  seed: string;
  baseSource: ProjectDataSourceDescriptor;
  candidateSource: ProjectDataSourceDescriptor;
}

export interface ProjectDataMergeCheckContext {
  generatedAt: string;
  baseSource: ProjectDataSourceDescriptor;
  candidateSource: ProjectDataSourceDescriptor;
  summary: {
    findings: number;
    conflicts: number;
    missingInCandidate: number;
    extraInCandidate: number;
    finalValidationFindings: number;
    baseExisting: number;
    candidateIntroduced: number;
    mergeInduced: number;
    addedFromSupplemental: number;
  };
  compareFindings: Array<Record<string, unknown>>;
  finalValidationFindings: Array<Record<string, unknown>>;
  lineage?: Array<{
    mergedPath: string;
    origin: "base" | "candidate";
    sourcePath: string;
  }>;
}

export interface ProjectDataComparisonSnapshot {
  baseSource: ProjectDataSourceDescriptor;
  candidateSource: ProjectDataSourceDescriptor;
  findings: unknown[];
  matches: Array<{
    basePath: string;
    candidatePath: string;
    confidence: number;
  }>;
  summary: {
    matches: number;
    findings: number;
    conflicts: number;
    missingInCandidate: number;
    extraInCandidate: number;
    finalValidationFindings?: number;
    baseExisting?: number;
    candidateIntroduced?: number;
    mergeInduced?: number;
  };
}

export interface ProjectDataSourceRef {
  sourceId: string;
  sourceLabel: string;
  jsonPath: string;
  entityType?: "project" | "itinerary_day" | "itinerary_item" | "arrangement";
  entityId?: string | number;
  evidence?: {
    fileName?: string;
    url?: string;
    sheet?: string;
    range?: string;
    row?: number | string;
    page?: number;
    blockIds?: string[];
    quote?: string;
  };
  confidence?: number;
}

export interface ProjectDataTraceIndex {
  refsByJsonPath: Record<string, ProjectDataSourceRef[]>;
  refsByEntityKey?: Record<string, ProjectDataSourceRef[]>;
}

export interface SourcedProjectData {
  data: AggregatedProjectData;
  source: ProjectDataSourceDescriptor;
  trace?: ProjectDataTraceIndex;
}

export interface ProjectDataConversionUsage {
  model?: string | null;
  tokens?: Record<string, unknown> | null;
  estimatedCost?: number | null;
}

export interface ProjectDataConversionResult {
  conversionId: string;
  source: ProjectDataSourceDescriptor;
  projectData: AggregatedProjectData;
  conversionReport: Record<string, unknown>;
  provenance: ProjectDataTraceIndex;
  usage?: ProjectDataConversionUsage;
  warnings: string[];
}

// --- Config types (mirror YAML structure) ---

/** チェック結果の出力言語 */
export type OutputLanguage = "en" | "ja";

/** systemPrompt バリアント別の言語指示テキスト */
export interface LanguageDirectiveVariants {
  standard: string;
  profile: string;
  compaction: string;
}

/** settings.yaml の languageDirectives セクションの型 */
export interface LanguageDirectivesConfig {
  en: LanguageDirectiveVariants;
  ja: LanguageDirectiveVariants;
}

export type CheckCategory = "general" | "need_review" | "reminders";

export type CheckImportance = "critical" | "high" | "medium" | "low";

export interface ModelConfig {
  model: string;
  /**
   * 送信先プロバイダ。省略時はモデル ID からレジストリで逆引きする
   * （現状どのモデル ID も一意なので、通常は指定不要）。
   * 同名モデルが両プロバイダに現れたときの曖昧さ解消のために持つ
   * （doc/planning/multi-provider-llm-plan.md §4.2 の {provider, modelId}）。
   */
  provider?: LlmProviderIdConfig;
  /**
   * 推論強度（共通語彙）。プロバイダごとの語彙へは clampEffort が丸める。
   *
   * **未指定はプロバイダの既定で、その既定は揃っていない**
   * （OpenAI は medium 相当、Anthropic の Sonnet 5 は adaptive thinking が ON）。
   * 思考分も出力として課金されるので、コストを見るなら明示すること。
   */
  effort?: "minimal" | "low" | "medium" | "high" | "max";
  temperature: number;
  maxTokens?: number;
}

export type SchemaDialect = "jtd" | "jsonschema";

export interface SchemaDefinition {
  dialect?: SchemaDialect | "jsonshcema";
  schema: Record<string, unknown>;
}

export interface AICheckConfig {
  id: string;
  name: string;
  description: string;
  category: CheckCategory;
  batchGroup?: string;
  enabled: boolean;
  executionOrder: number;
  importance: CheckImportance;
  dataSelectors: string[];
  promptTemplate: string;
  systemPrompt?: string;
  modelConfig?: Partial<ModelConfig>;
  outputSchema: SchemaDefinition;
  outputSchemaRef?: string;
  runAfterAll?: boolean;
  requiresCustomerProfile?: boolean;
  requiresMergeContext?: boolean;
  /**
   * 旅程計画ステージ（inquiry/proposal/quotation/booking/final/post）。
   * 指定時は案件が該当ステージのときのみ実行。空/未指定 = 全ステージ。
   */
  stages?: string[];
}

export type ComparisonOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "includes_any"
  | "includes_all"
  | "greater_than"
  | "less_than"
  | "is_empty"
  | "is_not_empty"
  | "date_before"
  | "date_after"
  | "custom"
  | "custom_js";

export type RuleSeverity = "error" | "warning" | "info";

export interface ConsistencyRuleConfig {
  id: string;
  name: string;
  description: string;
  category: CheckCategory;
  severity: RuleSeverity;
  enabled: boolean;
  executionOrder: number;
  importance: CheckImportance;
  requiresCustomerProfile?: boolean;
  sourceField: string;
  targetField?: string;
  operator: ComparisonOperator;
  customEvaluator?: string;
  script?: string;
  failureMessage: string;
  successMessage: string;
}

export interface BatchGroupConfig {
  id: string;
  name: string;
  description: string;
  systemPrompt?: string;
  modelConfig?: Partial<ModelConfig>;
}

// --- Freeform Review types ---

export type FreeformDataPolicy = "standard" | "minimal" | "full";

export interface FreeformCheckTemplate {
  id: string;
  stage: string;
  description: string;
  defaultPrompt: string;
  modelConfig?: Partial<ModelConfig>;
  dataPolicy?: FreeformDataPolicy;
}

export type FreeformFindingType =
  | "must_confirm"
  | "should_review"
  | "idea"
  | "data_gap";

export type FreeformFindingSeverity = "critical" | "warning" | "info";

export type FreeformFindingConfidence = "high" | "medium" | "low";

export interface FreeformEvidence {
  source: string;
  quote: string;
  json_path?: string | null;
}

export interface FreeformFinding {
  finding_type: FreeformFindingType;
  severity: FreeformFindingSeverity;
  confidence: FreeformFindingConfidence;
  finding: string;
  evidence: FreeformEvidence[];
  day_number?: number | null;
  item_reference?: string | null;
  json_path?: string | null;
  recommendation: string;
}

export interface FreeformCheckResult {
  templateId: string;
  templateName: string;
  stage: string;
  summary: string;
  findings: FreeformFinding[];
  reasoningSummary?: string;
  tokensUsed: { prompt: number; completion: number; total: number };
  model?: string;
  durationMs?: number;
  estimatedCost?: number;
  createdAt: string;
}

export interface FreeformPartialResult {
  templateId: string;
  templateName: string;
  stage: string;
  summary?: string;
  findings: FreeformFinding[];
  updatedAt?: string;
}

export interface FreeformProgressDetail {
  label: string;
  value: string;
  kind?: "text" | "code" | "json" | "log";
}

export type FreeformProgressEvent =
  | {
      type: "status";
      status:
        | "queued"
        | "validating"
        | "preparing-data"
        | "calling-model"
        | "completed";
      message: string;
      details?: FreeformProgressDetail[];
      createdAt?: string;
    }
  | {
      type: "tool-progress";
      eventType: string;
      message: string;
      itemId?: string;
      outputIndex?: number;
      details?: FreeformProgressDetail[];
      createdAt?: string;
    }
  | {
      type: "reasoning-summary";
      summary: string;
      delta?: string;
      details?: FreeformProgressDetail[];
      createdAt?: string;
    }
  | {
      type: "api-progress";
      phase: "waiting" | "streaming";
      bytesReceived?: number;
      tokensReceived?: number;
      contentLength?: number;
      createdAt?: string;
    }
  | {
      type: "partial-summary";
      summary: string;
      createdAt?: string;
    }
  | {
      type: "partial-finding";
      index: number;
      finding: FreeformFinding;
      createdAt?: string;
    }
  | {
      type: "result";
      data: FreeformCheckResult;
      createdAt?: string;
    }
  | {
      type: "error";
      error: string;
      createdAt?: string;
    }
  | {
      /** keep-alive のみ。UI では無視する */
      type: "heartbeat";
      ts: number;
      createdAt?: string;
    };

export interface ModelPricingEntry {
  model: string;
  input: number;
  output: number;
  /**
   * @deprecated cacheRead を使うこと。読み出し単価としてのみ解釈される。
   * 未設定時は input の 10%（OpenAI の既定割引）。
   */
  cachedInput?: number;
  /**
   * プロバイダ。省略時は "openai"（既存 settings.yaml との後方互換）。
   * 同名モデルがプロバイダ間で衝突しうるため、照合はこれを含めて行う。
   */
  provider?: string;
  /**
   * プロンプトキャッシュ**読み出し**の単価（USD/1M）。
   * 未設定時は cachedInput、それも無ければ input の 10%。
   * OpenAI / Anthropic とも概ね input の 10%。
   */
  cacheRead?: number;
  /**
   * プロンプトキャッシュ**書き込み**の単価（USD/1M）。
   * 未設定時は input（= 割増なし）。OpenAI は書き込みに追加課金がないため既定で正しいが、
   * **Anthropic は input の 1.25 倍（1h TTL なら 2 倍）の割増**なので必ず明示すること。
   * ここを OpenAI と同列に扱うとコストを過小見積もりする。
   */
  cacheWrite?: number;
  contextWindow: number;
  maxOutputTokens: number;
}

/** Embedding プロバイダ。Anthropic は embeddings API を持たないため候補に無い。 */
export type EmbeddingProviderId = "openai" | "voyage" | "cohere" | "gemini";

/**
 * LLM プロバイダ。`llm/registry.ts` の `LlmProviderId` と同一だが、型定義が
 * ランタイム実装に依存しないようここにも置く（設定型は types 側で閉じる）。
 */
export type LlmProviderIdConfig = "openai" | "anthropic";

export interface EmbeddingSettingsConfig {
  /** 既定 "openai"。 */
  provider?: EmbeddingProviderId;
  /** 未指定なら provider ごとの env（OpenAI は OPENAI_API_KEY）から解決する。 */
  apiKey?: string;
  /** 既定 "text-embedding-3-small"。 */
  model?: string;
  /**
   * ベクトル次元。保存済みベクトルとの整合判定に使う（実際の次元は API 応答が正）。
   * 既定 1536（text-embedding-3-small）。
   */
  dimensions?: number;
}

export interface ExpertKnowledgeSettingsConfig {
  /** Cosine ≥ this → auto-absorb without LLM. Default: 0.86 */
  autoAbsorbThreshold?: number;
  /** Cosine in [this, autoAbsorbThreshold) → LLM adjudicates. Default: 0.65 */
  adjudicationThreshold?: number;
  /** Fact embedding similarity required to trigger LLM relation check. Default: 0.90 */
  factMatchThreshold?: number;
  /** Minimum cosine for relatedAspectHint → aspect link. Default: 0.65 */
  aspectRefThreshold?: number;
  /**
   * Maximum number of matching aspects injected per run when projectData is
   * supplied to buildExpertKnowledgeRulesPrompt.  Aspects are ordered by
   * state (live first), then utility desc, then evidenceCount desc before
   * capping.  Default: 20.
   */
  maxInjectedAspects?: number;
  /**
   * Interval (days) for scheduled aspect distillation triggered from the
   * maintenance sweep. 0 (default) disables the scheduled trigger; the manual
   * button in the approval queue is always available. Scheduled runs
   * additionally require at least 5 non-retired aspects.
   */
  distillationIntervalDays?: number;
  /**
   * Number of days a low-utility retire proposal may sit unprocessed in the
   * approval queue before the maintenance sweep auto-applies it (audit actor
   * "system:auto-retire"). Default: 30. Set to 0 (or negative) to disable
   * auto-retire entirely (proposals then wait for a manual decision).
   */
  autoRetireAfterDays?: number;
}

export interface SettingsConfig {
  cacheKeyringJson?: string;
  cacheActiveKid?: string;
  openai: {
    apiKey: string;
    defaultModel: string;
    defaultTemperature: number;
    defaultMaxTokens: number;
    /**
     * 表示専用の軽量要約（ひとこと・ナレーション等）に使う最安モデル。
     * 旧ローカル LLM（qwen3）の置き換え先。既定 gpt-5-nano。
     * Anthropic に該当する廉価ティアがないため OpenAI 固定
     * （doc/planning/multi-provider-llm-plan.md §2）。
     */
    lightModel?: string;
  };
  /**
   * Anthropic 側の資格情報と既定モデル。`openai` と対称の形にしてある
   * （doc/planning/multi-provider-llm-plan.md §4.1）。
   * temperature の既定は持たない —— Opus 5 / Sonnet 5 は送ると 400 になるため。
   */
  anthropic?: {
    apiKey?: string;
    defaultModel?: string;
    defaultMaxTokens?: number;
  };
  /**
   * プロバイダ横断の既定。モデル単位の指定（check / batchGroup）が無いときに
   * どちらへ送るかを決める。既定は "openai"（段階移行のため挙動不変）。
   */
  llm?: {
    defaultProvider?: LlmProviderIdConfig;
    /**
     * LLM 呼び出し・チェック実行の失敗ログを Slack アラート（CloudWatch の
     * 文言マッチ）に乗せるか。既定 false = 監視語をマスクして記録のみ。
     * 短時間の LLM outage はそれなりの頻度で起きるため、既定は静音。
     * 管理者はアプリ内通知（AI Check 画面）と /usage で気づける。
     */
    failureAlerts?: boolean;
    /**
     * プロンプトキャッシュ（Anthropic のみ）。
     *
     * OpenAI は自動キャッシュなので設定不要。Anthropic は `cache_control` を
     * 明示しない限り一切キャッシュされない（現状 cached 2.5% の主因）。
     *
     * **既定は無効**。効くのは「同一案件・データ未変更での再実行」に限られ、
     * 一方でキャッシュ**書き込み**は 1.25 倍の割増課金になる。ヒット率が
     * 低ければ純粋な値上げにしかならないため、実測してから既定 ON を判断する
     * （doc/planning/multi-provider-llm-plan.md §3）。
     */
    promptCache?: {
      enabled?: boolean;
      /** TTL。"1h" は書き込みが 2 倍課金。既定 "5m"。 */
      ttl?: "5m" | "1h";
    };
  };
  /**
   * Embedding は LLM とは別系統（doc/planning/multi-provider-llm-plan.md §4.1）。
   * Anthropic は embeddings API を提供しないため、LLM を Anthropic に寄せても
   * ここは独立に選ぶ。既定は OpenAI（月 $0.001 未満で、コストは選定理由にならない）。
   */
  embedding?: EmbeddingSettingsConfig;
  /** Expert knowledge pipeline thresholds. */
  expertKnowledge?: ExpertKnowledgeSettingsConfig;
  defaultSystemPrompt: string;
  tokenBudget?: {
    maxTokensPerRun: number;
  };
  parallelExecution?: {
    maxConcurrent: number;
    minStartIntervalMs: number;
  };
  modelPricing: ModelPricingEntry[];
  contextOverflowFallback: boolean;
  globalPromptDirectives?: string[];
  idleTimeoutMinutes?: number;
  /** systemPrompt 内の {{language_directive}} プレースホルダーに展開される言語別指示。必須 */
  languageDirectives: LanguageDirectivesConfig; // ? なし — 必須フィールド
}

// --- Contact topics compaction types ---

export interface EvidenceSource {
  source_id: string; // "contact_{id}"
  source_type: string; // contact_type (email, phone_call, etc.)
  contacted_at: string; // 元レコードのcontacted_at
  contact_purpose: string; // 元レコードのcontact_purpose
  quote: string; // 関連部分の引用
}

export interface TopicEvidence {
  evidence_id: string;
  evidence_summary: string;
  sources: EvidenceSource[];
}

export interface ContactTopic {
  topic_id: string; // "top_dietary_request" 等
  topic_summary: string;
  evidences: TopicEvidence[];
}

export interface ContactTopicsResult {
  contactTopics: ContactTopic[];
  tokensUsed: { prompt: number; completion: number; total: number };
  model?: string;
  fallbackFrom?: string;
  durationMs?: number;
  lastCheckedAt: string; // 処理済み最新のupdated_at
  error?: string;
  compactionDegraded?: boolean;
}

export interface ContactCompactionConfig {
  enabled: boolean;
  promptTemplate: string;
  incrementalPromptTemplate: string;
  systemPrompt?: string;
  modelConfig?: Partial<ModelConfig>;
  outputSchema: SchemaDefinition;
}

// --- Customer profile config ---

export interface CustomerProfileConfig {
  enabled: boolean;
  dataSelectors: string[];
  promptTemplate: string;
  systemPrompt?: string;
  modelConfig?: Partial<ModelConfig>;
  outputSchema: SchemaDefinition;
  /**
   * Anthropic の strict 出力（文法サイズ上限 ≈ 61 props）に収めるための
   * トップレベルプロパティのグループ分け。指定があり、かつプロバイダが
   * Anthropic のときは、グループごとにサブスキーマで strict 抽出して
   * マージする。全トップレベルプロパティを重複なく網羅していない場合は
   * 無効として従来の単一呼び出しに落ちる。
   */
  strictSplitGroups?: string[][];
  compaction?: ContactCompactionConfig;
}

export interface CustomerProfileResult {
  profile: Record<string, unknown> | null;
  /**
   * スキーマと合わなかった箇所（先頭 20 件）。undefined = 合致、または未検証。
   *
   * Anthropic では strict 出力が文法サイズ上限を超えるため外れており、
   * 形はプロンプト指示だけが担保している。ずれを黙って通さないための記録。
   */
  schemaIssues?: string[];
  tokensUsed: { prompt: number; completion: number; total: number };
  model?: string;
  fallbackFrom?: string;
  durationMs?: number;
  error?: string;
  extractedAt: string;
}

// --- Shared schema types ---

export interface SharedSchemaEntry {
  id: string;
  name: string;
  description?: string;
  schema: SchemaDefinition;
}

export interface SharedSchemasConfig {
  schemas: SharedSchemaEntry[];
}

export interface FullConfig {
  settings: SettingsConfig;
  aiChecks: AICheckConfig[];
  consistencyRules: ConsistencyRuleConfig[];
  batchGroups: BatchGroupConfig[];
  customerProfile: CustomerProfileConfig;
  sharedSchemas: SharedSchemasConfig;
}

// --- Rate limit info (from OpenAI response headers) ---

export interface RateLimitInfo {
  limitRequests: number;
  limitTokens: number;
  remainingRequests: number;
  remainingTokens: number;
  resetRequests: string;
  resetTokens: string;
  capturedAt: string;
}

// --- Result types ---

export type CheckStatus = "pass" | "warning" | "fail" | "error" | "skipped";

export interface AICheckResult {
  checkId: string;
  checkName: string;
  category: CheckCategory;
  status: CheckStatus;
  result: Record<string, unknown>;
  overallScore?: number;
  summary?: string;
  error?: string;
  importance?: CheckImportance;
  rawAIScore?: number; // Original AI score before adjustment (only if adjusted)
  scoreAdjusted?: boolean; // true if server-side ceiling was applied
  tokensUsed: { prompt: number; completion: number; total: number };
  model?: string;
  fallbackFrom?: string;
  durationMs?: number;
  batchGroupId?: string;
  batchGroupName?: string;
}

export interface ConsistencyCheckResult {
  ruleId: string;
  ruleName: string;
  category: CheckCategory;
  severity: RuleSeverity;
  passed: boolean;
  message: string;
  importance?: CheckImportance;
  sourceValue?: unknown;
  targetValue?: unknown;
  evaluationError?: boolean;
}

export interface ModelUsageEntry {
  model: string;
  tokensUsed: { prompt: number; completion: number; total: number };
  durationMs: number;
  callCount: number;
  estimatedCost: number;
}

/** HMAC-SHA256 signatures for cache integrity verification */
export interface CacheSignatures {
  kid: string;
  aiResults: string;
  consistencyResults: string;
  customerProfile: string;
  contactTopics: string;
}

export interface CheckpointSignedValue<T> {
  data: T;
  signature?: string;
}

export interface CheckpointAIResultEntry {
  result: AICheckResult;
  signature?: string;
}

export interface PipelineNodeMemberEntry {
  kind: PipelineNodeType;
  checkIds?: string[];
  ruleIds?: string[];
}

export type PipelineNodeMembers = Record<string, PipelineNodeMemberEntry>;

export interface ScheduledAdminControl {
  mode: "cancel_before" | "cancel_after" | "cancel_now";
  targetNodeId?: string;
  requestedAt: string;
  status?: "scheduled" | "cancelled" | "stale";
}

export type NodeExecutionDirectiveState = "scheduled" | "stale";

export interface NodeExecutionDirective {
  rootNodeId: string;
  mode: "blocked_subtree";
  requestedAt: string;
  state: NodeExecutionDirectiveState;
}

export type PipelineNodeRuntimeStatus =
  | "planned"
  | "pending"
  | "active"
  | "completed"
  | "failed"
  | "skipped"
  | "aborted"
  | "blocked";

export interface AICheckRunCheckpoint {
  projectId: number;
  language: OutputLanguage;
  startedAt: string;
  updatedAt: string;
  signatureKid?: string;
  projectDataSnapshot?: AggregatedProjectData;
  projectDataSource?: ProjectDataSourceDescriptor;
  projectDataSignature?: string;
  plannedCheckIds?: string[];
  plannedCheckNameById?: Record<string, string>;
  checkLaneMap?: Record<string, CheckLane>;
  checkBatchGroups?: BatchGroupInfo[];
  graphSkeleton?: PipelineGraphSkeleton;
  completedCheckIds: string[];
  aiResults: CheckpointAIResultEntry[];
  consistencyResults: ConsistencyCheckResult[];
  customerProfileResult?: CheckpointSignedValue<CustomerProfileResult | null>;
  contactTopicsResult?: CheckpointSignedValue<ContactTopicsResult | null>;
  failedStage?: FailedStage;
  failedChecks?: FailedCheckInfo[];
  skippedItems?: SkippedItem[];
  lastKnownStatus?: RunStatus;
  nodeMembers?: PipelineNodeMembers;
  nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
  nodeExecutionDirectives?: NodeExecutionDirective[];
  nodeControlVersion?: number;
  scheduledAdminControl?: ScheduledAdminControl | null;
}

export type RunConflictReason =
  | "run_not_found"
  | "checkpoint_project_data_invalid"
  | "checkpoint_ai_results_unusable"
  | "target_already_active"
  | "target_already_completed"
  | "target_already_terminal"
  | "target_not_active"
  | "target_not_abortable"
  | "node_control_version_conflict"
  | "snapshot_unavailable";

export interface RunConflictResponse {
  ok: false;
  reason: RunConflictReason | string;
  message?: string;
}

export interface AICheckRunResult {
  projectId: number;
  startedAt: string;
  completedAt: string;
  status: "completed" | "error" | "cancelled";
  projectData: AggregatedProjectData;
  projectDataSource?: ProjectDataSourceDescriptor;
  projectDataComparison?: ProjectDataComparisonSnapshot;
  aiResults: AICheckResult[];
  consistencyResults: ConsistencyCheckResult[];
  customerProfileResult: CustomerProfileResult | null;
  contactTopicsResult?: ContactTopicsResult | null;
  totalTokensUsed: { prompt: number; completion: number; total: number };
  modelUsage?: ModelUsageEntry[];
  error?: string;
  /** Pipeline graph lane assignments for DAG reconstruction from cache */
  checkLaneMap?: Record<string, CheckLane>;
  /** Ordered list of planned check names for DAG reconstruction from cache */
  plannedChecks?: string[];
  /** Ordered list of planned check IDs aligned with plannedChecks */
  plannedCheckIds?: string[];
  /** Check ID -> check name mapping for rerun targeting from cache */
  plannedCheckNameById?: Record<string, string>;
  /** Server-generated graph skeleton (authoritative source for nodes/edges) */
  graphSkeleton?: PipelineGraphSkeleton;
  /** Server-generated node -> member mapping for admin rerun/cancel targeting */
  nodeMembers?: PipelineNodeMembers;
  /** Batch group definitions for DAG reconstruction from cache */
  checkBatchGroups?: BatchGroupInfo[];
  /** Independent consistency rule results (for DAG node display from cache) */
  independentConsistencyRules?: Array<{ name: string; passed: boolean }>;
  /** Profile-dependent consistency rule results (for DAG node display from cache) */
  profileConsistencyRules?: Array<{ name: string; passed: boolean }>;
  /** 実行時の出力言語。必須。省略不可 */
  language: OutputLanguage;
  /** Server-generated HMAC signatures for tamper detection on re-run */
  _signatures?: CacheSignatures;
  /**
   * 実行 ID。履歴ロード時にサーバ (`/latest` の `run.runId`) から付与される。
   * 指摘要約 (FindingsAiSummary) など run 単位の付随機能が参照する。ライブ実行直後は
   * 付かない場合がある（その場合は要約は次回のロードで表示）。
   */
  runId?: string;
}

/** Incremental data accumulated during a run. Never cached or saved. */
export interface PartialRunData {
  projectId: number;
  startedAt: string;
  projectData: AggregatedProjectData | null;
  projectDataSource?: ProjectDataSourceDescriptor;
  aiResults: AICheckResult[];
  consistencyResults: ConsistencyCheckResult[];
  customerProfileResult: CustomerProfileResult | null;
  contactTopicsResult: ContactTopicsResult | null;
}

// --- Run options ---

export interface RunOptions {
  projectId: number;
  jwt: string;
  scopeKey?: string;
  startedAt?: string;
  checkIds?: string[];
  skipProfile?: boolean;
  skipCompaction?: boolean;
  reevaluateConsistency?: boolean;
  previousResult?: AICheckRunResult | null;
  previousContactTopics?: ContactTopicsResult | null;
  projectDataOverride?: AggregatedProjectData;
  projectDataSource?: ProjectDataSourceDescriptor;
  tokenBudget?: number;
  dismissedCheckIds?: string[];
  runId?: string;
  /**
   * 実行が属する環境キー（dev/stg/...）。省略時は jwt の iss から解決する。
   * jwt を持たないサーバ起動の実行（スナップショットチェック等）では、
   * 案件の projectKey から解決した値を明示的に渡すこと。null のままだと
   * 環境を跨いだ実行の取り違えを防げない。
   */
  envKey?: string | null;
  /**
   * 実行を開始した人（email）。停止のような破壊的操作の認可に使う。
   * 観測は環境が一致すれば許すが、停止は「自分の実行」か管理者に限る。
   */
  startedBy?: string | null;
  /**
   * 案件の同一性（`crm:dev:42` / `ext:<digest>`）。表示 ID だけでは案件を
   * 特定できない — 外部案件の表示 ID は digest から導いた数値で、CRM 案件の
   * ID と衝突しうる。重複チェックはこれで絞る。
   */
  projectKey?: string | null;
  /**
   * 実行の同一性が確定した時点で呼ばれる。新規実行なら自分の runId、
   * 既存実行への相乗りなら「先に走っている実行」の runId が渡る。
   * 結果を永続化する呼び出し元は、自分で採番した runId ではなくここで
   * 受け取った runId を使うこと。使わないと同じ実行が別 ID で二重に残る。
   */
  onAttached?: (identity: {
    runId: string;
    projectId: number;
    language: OutputLanguage;
    startedAt: string;
  }) => void;
  /**
   * 進捗イベントを listener へ配る**前に**通す変換を作る。
   *
   * 署名の付与（チェックポイント用）、`check-result` → `check-result-ready`
   * への差し替えとチェック結果キャッシュへの保存、実行状態レジストリの更新は
   * すべてここで行われる。**これを通さないと、観測者は署名の無い生イベントを
   * 受け取る。** チェックポイントは署名の無い結果を保存できないため、次の
   * 再開で「完了していたはずのチェック」が最初からやり直しになる。
   *
   * listener ごとではなく run ごとに一度だけ通す。listener 側に置くと、
   * 観測者の数だけキャッシュ保存とレジストリ更新が走る。
   */
  createProgressSink?: (params: {
    runId: string;
    scopeKey: string;
    emit: (event: Record<string, unknown>) => Promise<void>;
  }) => (event: Record<string, unknown>) => Promise<void>;
  trackInFlight?: boolean;
  /** 案件の現在ステージ。指定時はステージ対象外のチェックを除外する */
  stage?: string | null;
  isNodeBlocked?: (nodeId: string) => boolean;
  beforeNodeStart?: (nodeId: string) => void | Promise<void>;
  afterNodeComplete?: (nodeId: string) => void | Promise<void>;
  /**
   * run 完了時に必ず一度だけ呼ばれる。**永続化はここで行う。**
   *
   * リクエストハンドラ側で保存すると、ブラウザのリロードで進捗ストリームが
   * 切れた時点で書き込みに到達せず、結果が失われる（実際に dev で発生）。
   * このフックは進捗ストリームから切り離された promise チェーンで実行される
   * ため、クライアントの接続状態に依存しない。
   *
   * 例外はここで握り潰される（保存の失敗で run 自体を失敗させない）。
   */
  onCompleted?: (result: AICheckRunResult) => void | Promise<void>;
  /** チェック結果の出力言語。必須。省略不可 */
  language: OutputLanguage;
}

// --- localStorage types ---

export interface AICheckRunHistoryEntry {
  projectId: number;
  /** 履歴リンク表示用のプロジェクトタイトル */
  projectTitle?: string;
  runAt: string;
  status: "completed" | "error" | "cancelled";
  /** 実行時の出力言語。言語バッジ表示に使用 */
  language?: OutputLanguage;
  /** 外部データチェックの場合のソース表示名 */
  sourceLabel?: string;
  /** 外部データチェックの場合のソース種別 */
  sourceKind?: ProjectDataSourceKind;
  /** 変換キャッシュと紐づけられる場合のソースキャッシュキー */
  sourceCacheKey?: string;
  /** CRM API 以外の projectData を使ったチェックかどうか */
  usesExternalProjectData?: boolean;
  aiResultCount: number;
  consistencyResultCount: number;
  passCount: number;
  warningCount: number;
  failCount: number;
}

// --- Evidence (AI check findings) ---

export interface Evidence {
  /** CRM field path or data section (e.g. "customer_profile.dietary") */
  source: string;
  /** Relevant excerpt. For missing/absent data, describes what was expected but not found. */
  quote: string;
}

export interface SummarySection {
  text: string;
  citation_indices: number[];
}

export type TraceabilityTab =
  | "basic"
  | "review"
  | "reminders"
  | "expert-knowledge";

export interface FocusedEvidenceTarget {
  checkId: string;
  evidenceIndex: number;
}

export interface SummaryReturnTarget {
  tab: TraceabilityTab;
  checkId: string;
  findingIndex: number;
}

export interface TraceabilityHistoryState {
  kind: "tab" | "summary-finding" | "check" | "evidence";
  tab: TraceabilityTab;
  summaryFindingIndex?: number;
  checkId?: string;
  evidenceIndex?: number;
  returnFindingIndex?: number;
}

export interface BasicRequestsSummaryFinding {
  severity: "critical" | "warning" | "info";
  finding: string;
  evidence: string;
  recommendation: string;
  check_point?: string;
  day_number?: number;
  item_type?: string;
  source_check_ids?: string[];
  evidence_refs?: number[];
}

export interface BasicRequestsSummaryResult {
  overall_score: number;
  summary: string;
  summary_sections?: SummarySection[];
  findings: BasicRequestsSummaryFinding[];
}

export function sanitizeCitationIndices(
  indices: unknown,
  findingsLength: number,
): number[] {
  if (!Array.isArray(indices)) return [];
  const valid = indices
    .filter((i): i is number => typeof i === "number" && Number.isInteger(i))
    .filter((i) => i >= 0 && i < findingsLength);
  return [...new Set(valid)];
}

export function buildCitationNumberMap(
  sections: SummarySection[],
  findingsLength: number,
): Map<number, number> {
  const map = new Map<number, number>();
  let nextNumber = 1;

  for (const section of sections) {
    for (const index of sanitizeCitationIndices(
      section.citation_indices,
      findingsLength,
    )) {
      if (!map.has(index)) {
        map.set(index, nextNumber);
        nextNumber += 1;
      }
    }
  }

  return map;
}

export function sanitizeEvidenceRefs(
  indices: unknown,
  evidenceCount: number,
): number[] {
  if (!Array.isArray(indices)) return [];
  const valid = indices
    .filter((i): i is number => typeof i === "number" && Number.isInteger(i))
    .filter((i) => i >= 0 && i < evidenceCount);
  return [...new Set(valid)];
}

// --- Extracted notes (customer profile) ---

export interface ExtractedNoteSource {
  source_field: string;
  type: string;
  date: string;
  excerpt: string;
}

export type ExtractedNoteImportance = "high" | "medium" | "low";

export interface ExtractedNote {
  topic: string;
  importance: ExtractedNoteImportance;
  summary: string;
  detail: string;
  sources: ExtractedNoteSource[];
}

// --- Cache versioning ---

export type CacheStaleReason =
  | "version_mismatch"
  | "legacy"
  | "parse_error"
  | "invalid";

export interface CacheGetResult {
  result: AICheckRunResult | null;
  staleReason: CacheStaleReason | null;
}

// --- Run state (for Zustand store) ---

export type RunStatus =
  | "idle"
  | "fetching-data"
  | "compacting-contacts"
  | "running-consistency"
  | "extracting-profile"
  | "running-ai"
  | "completed"
  | "aborted"
  | "error"
  | "cancelled"
  | "reconnecting"
  | "disconnected";

export interface ApiProgressInfo {
  checkName: string;
  batchGroupId?: string;
  phase: "waiting" | "streaming";
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  attempt?: number;
}

export interface FailedCheckInfo {
  checkId: string;
  checkName: string;
  batchGroupId?: string;
  batchGroupName?: string;
  rerunCheckIds?: string[];
  error: string;
}

export type FailedStage = "compaction" | "profile" | "ai-checks";

export type CheckLane = "independent" | "dependent" | "afterAll";

export type PipelineLane = "pre-fork" | "lane-a" | "lane-b" | "post-join";

export interface BatchGroupInfo {
  id: string;
  name: string;
  checks: string[];
}

export type PipelineNodeType =
  | "system"
  | "ai-check"
  | "consistency"
  | "summary";

export interface PipelineSkeletonNode {
  id: string;
  nodeType: PipelineNodeType;
  label: string;
  lane: PipelineLane;
  checks?: string[];
  batchSize?: number;
  ruleCount?: number;
  passedCount?: number;
  failedCount?: number;
  warningCount?: number;
}

export interface PipelineSkeletonEdge {
  id: string;
  source: string;
  target: string;
}

export interface PipelineGraphSkeleton {
  nodes: PipelineSkeletonNode[];
  edges: PipelineSkeletonEdge[];
}

export interface SkippedItem {
  id: string;
  name: string;
  kind: "ai-check" | "consistency-rule";
}

export interface RunProgress {
  status: RunStatus;
  currentStep: string;
  completedChecks: number;
  totalChecks: number;
  error?: string;
  startedAt?: number;
  apiStatus?: string;
  apiProgress?: ApiProgressInfo;
  activeChecks?: ApiProgressInfo[];
  plannedChecks?: string[];
  completedCheckNames?: string[];
  failedStage?: FailedStage;
  failedChecks?: FailedCheckInfo[];
  stageError?: string;
  skippedItems?: SkippedItem[];
  checkLaneMap?: Record<string, CheckLane>;
  /** Batch group membership: maps individual check name → batch group id */
  checkBatchGroups?: BatchGroupInfo[];
  consistencyRuleResults?: { name: string; passed: boolean }[];
  profileConsistencyRuleResults?: { name: string; passed: boolean }[];
  plannedCheckIds?: string[];
  plannedCheckNameById?: Record<string, string>;
  nodeMembers?: PipelineNodeMembers;
  nodeStates?: Record<string, PipelineNodeRuntimeStatus>;
  nodeExecutionDirectives?: NodeExecutionDirective[];
  nodeControlVersion?: number;
  scheduledAdminControl?: ScheduledAdminControl | null;
  lastRecoveryError?: string | null;
  /** Server-generated graph skeleton (authoritative source for nodes/edges) */
  graphSkeleton?: PipelineGraphSkeleton;
  /** Client-side hint for preserving upstream graph state during selective reruns */
  selectiveRerun?: {
    selectedCheckIds: string[];
    affectedCheckNames: string[];
    preserveFetch: boolean;
    preserveIndependentConsistency: boolean;
    preserveCompaction: boolean;
    preserveProfile: boolean;
    preserveProfileConsistency: boolean;
  };
}

export type AdminNodeAction =
  | "abort_node"
  | "toggle_abort_from_here"
  | "rerun_from_node";

export type NodeControlOutcome =
  | "scheduled"
  | "cleared"
  | "aborted"
  | "started";

export type AdminControlSuccessResponse =
  | {
      ok: true;
      action: "toggle_abort_from_here";
      outcome: "scheduled" | "cleared";
      runId: string;
      nodeControlVersion: number;
      nodeExecutionDirectives: NodeExecutionDirective[];
      affectedNodeIds: string[];
    }
  | {
      ok: true;
      action: "abort_node";
      outcome: "aborted";
      runId: string;
      nodeControlVersion: number;
      nodeExecutionDirectives: NodeExecutionDirective[];
      affectedNodeIds: string[];
      abortedNodeId: string;
    }
  | {
      ok: true;
      action: "rerun_from_node";
      outcome: "started";
      runId: string;
      startedAt: string;
      previousRunId: string | null;
      snapshotSource:
        | "server_cache"
        | "final_result_cache"
        | "browser_checkpoint";
      invalidatedNodeIds: string[];
      preservedNodeIds: string[];
      checkpointSeed: AICheckRunCheckpoint;
    };
